import type { ProjectTimeEntry, TimberProject } from '../types';

export function isOngoingBillableProject(project?: TimberProject | null): boolean {
  return project?.phase === 'preconstruction' && !!project.ongoingBillable;
}

export type ProjectBillingLabel = 'Billable' | 'Non-billable' | 'Contract';

export function getProjectBillingLabel(project?: TimberProject | null): ProjectBillingLabel {
  if (!project || project.phase !== 'preconstruction') return 'Contract';
  return project.ongoingBillable ? 'Billable' : 'Non-billable';
}

export type BillableProjectUserRow = {
  userId: string;
  userName: string;
  minutes: number;
};

export type BillableProjectReportRow = {
  projectId: string;
  projectName: string;
  clientName: string;
  billingLabel: ProjectBillingLabel;
  isBillable: boolean;
  minutes: number;
  byUser: BillableProjectUserRow[];
};

export type BillableProjectTimeReport = {
  rows: BillableProjectReportRow[];
  totalMinutes: number;
  billableMinutes: number;
  nonBillableMinutes: number;
  billableProjectCount: number;
};

/** Snap raw minutes to the nearest 15-minute billing increment. */
export function roundReportMinutes(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

/** Quarter-hour rounding for report row hours. */
export function formatReportHours(minutes: number): string {
  const hrs = roundReportMinutes(minutes) / 60;
  return `${hrs % 1 === 0 ? hrs.toFixed(0) : hrs}h`;
}

/** Total shown in header/footer — sum of per-row rounded minutes so displayed numbers add up. */
export function sumDisplayReportMinutes(rowMinutes: number[]): number {
  return rowMinutes.reduce((s, m) => s + roundReportMinutes(m), 0);
}

/** Users with at least one billable quarter-hour after rounding. */
export function filterDisplayReportUsers(users: BillableProjectUserRow[]): BillableProjectUserRow[] {
  return users.filter((u) => roundReportMinutes(u.minutes) > 0);
}

/** Billable quarter-hours for a project — round combined raw project time once (not per-user sum). */
export function projectRowDisplayMinutes(row: BillableProjectReportRow): number {
  return roundReportMinutes(row.minutes);
}

export type BillableProjectUserDisplayRow = BillableProjectUserRow & {
  /** Quarter-hour slice of the project total, proportional to raw time — sums to project total. */
  displayMinutes: number;
};

/**
 * Split a project's rounded billable total across users by raw share (largest-remainder on 15-min units).
 * Ensures displayed user hours sum to the project row total.
 */
export function allocateUserDisplayMinutes(row: BillableProjectReportRow): BillableProjectUserDisplayRow[] {
  const active = row.byUser.filter((u) => u.minutes > 0);
  const projectMinutes = projectRowDisplayMinutes(row);
  if (active.length === 0 || projectMinutes <= 0) return [];

  const totalQuarters = projectMinutes / 15;
  const rawSum = active.reduce((s, u) => s + u.minutes, 0);
  if (rawSum <= 0) return [];

  const exact = active.map((u) => (totalQuarters * u.minutes) / rawSum);
  const floors = exact.map((q) => Math.floor(q));
  let remainder = Math.round(totalQuarters - floors.reduce((s, q) => s + q, 0));

  const order = exact
    .map((q, i) => ({ i, frac: q - floors[i] }))
    .sort((a, b) => b.frac - a.frac);

  const quarters = [...floors];
  for (let r = 0; remainder > 0; r += 1) {
    quarters[order[r % order.length].i] += 1;
    remainder -= 1;
  }

  return active
    .map((u, i) => ({ ...u, displayMinutes: quarters[i] * 15 }))
    .filter((u) => u.displayMinutes > 0);
}

export function buildBillableProjectTimeReport(args: {
  entries: ProjectTimeEntry[];
  projects: TimberProject[];
  showNonBillable: boolean;
  scopeProjectId?: string;
}): BillableProjectTimeReport {
  const { entries, projects, showNonBillable, scopeProjectId } = args;
  const projectById = new Map(projects.map((p) => [p.id, p]));

  const byProjectId = new Map<string, Map<string, BillableProjectUserRow>>();

  for (const entry of entries) {
    if (scopeProjectId && entry.projectId !== scopeProjectId) continue;

    let users = byProjectId.get(entry.projectId);
    if (!users) {
      users = new Map();
      byProjectId.set(entry.projectId, users);
    }
    const uid = entry.userId || 'unknown';
    const existing = users.get(uid);
    if (existing) {
      existing.minutes += entry.minutes;
    } else {
      users.set(uid, {
        userId: uid,
        userName: entry.userName || 'Unknown',
        minutes: entry.minutes,
      });
    }
  }

  let billableMinutes = 0;
  let nonBillableMinutes = 0;

  const allRows: BillableProjectReportRow[] = [];

  for (const [projectId, userMap] of Array.from(byProjectId.entries())) {
    const proj = projectById.get(projectId);
    const isBillable = isOngoingBillableProject(proj);
    const billingLabel = getProjectBillingLabel(proj);
    const byUser: BillableProjectUserRow[] = Array.from(userMap.values()).sort(
      (a, b) => b.minutes - a.minutes,
    );
    const minutes = byUser.reduce((s, u) => s + u.minutes, 0);

    if (isBillable) billableMinutes += minutes;
    else nonBillableMinutes += minutes;

    allRows.push({
      projectId,
      projectName: proj?.name || 'Unknown',
      clientName: proj?.clientName || '',
      billingLabel,
      isBillable,
      minutes,
      byUser,
    });
  }

  allRows.sort((a, b) => b.minutes - a.minutes);

  const rows = scopeProjectId
    ? allRows.filter((r) => r.projectId === scopeProjectId)
    : showNonBillable
      ? allRows
      : allRows.filter((r) => r.isBillable);

  const totalMinutes = rows.reduce((s, r) => s + r.minutes, 0);
  const billableProjectCount = rows.filter((r) => r.isBillable).length;

  return {
    rows,
    totalMinutes,
    billableMinutes,
    nonBillableMinutes,
    billableProjectCount,
  };
}
